// @ts-nocheck
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Calendar,
  DollarSign,
  Package,
  User,
  Phone,
  Mail,
  MapPin,
  Clock,
  Link,
  Upload,
  MessageSquare
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface VideoOrder {
  id: string;
  purchaser_name: string;
  purchaser_email: string;
  purchaser_phone: string;
  player_name: string;
  team_name?: string;
  status: string;
  total_amount_cents: number;
  created_at: string;
  updated_at: string;
  notes?: string;
  video_url?: string;
  vendor?: {
    business_name?: string;
    email?: string;
  };
  event?: {
    name: string;
    start_date: string;
  };
  package?: {
    package_type: string;
    price_cents: number;
    description: string;
  };
}

interface OrderDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: VideoOrder | null;
  onOrderUpdate?: () => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  open,
  onOpenChange,
  order,
  onOrderUpdate,
}) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [revisionNote, setRevisionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  if (!order) return null;

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'in_progress':
        return 'default';
      case 'confirmed':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'cancelled':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const handleVideoSubmit = async () => {
    if (!videoUrl.trim()) {
      toast.error('Please enter a video URL');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('video_orders')
        .update({ 
          video_url: videoUrl.trim(),
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', order.id);

      if (error) throw error;

      // Send notification email to customer
      try {
        await invokeEdgeFunction('send-email', {
          body: {
            email_type: 'custom_notification',
            to_email: order.purchaser_email,
            data: {
              name: order.purchaser_name,
              subject: 'Your Video is Ready!',
              message: `
                <h2>Your video is ready!</h2>
                <p>Hi ${order.purchaser_name},</p>
                <p>Your video for ${order.player_name} has been completed and is ready to view.</p>
                <p><strong>Video URL:</strong> <a href="${videoUrl.trim()}" target="_blank">${videoUrl.trim()}</a></p>
                <p>If you have any questions or need revisions, please contact us.</p>
                <p>Best regards,<br>The RecruitLook Team</p>
              `,
              player_name: order.player_name
            }
          }
        });
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
      }

      // Send SMS notification if phone number is available
      if (order.purchaser_phone) {
        try {
          await invokeEdgeFunction('send-sms', {
            body: {
              to_phone: order.purchaser_phone,
              message: `Hi ${order.purchaser_name}! Your video for ${order.player_name} is ready to view: ${videoUrl.trim()}`
            }
          });
        } catch (smsError) {
          console.error('Failed to send SMS notification:', smsError);
        }
      }

      toast.success('Video URL submitted successfully!');
      setVideoUrl('');
      onOrderUpdate?.();
    } catch (error) {
      console.error('Error submitting video:', error);
      toast.error('Failed to submit video URL');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevisionRequest = async () => {
    if (!revisionNote.trim()) {
      toast.error('Please enter revision details');
      return;
    }

    setIsSubmitting(true);
    try {
      // Update order status and add revision note
      const { error } = await supabase
        .from('video_orders')
        .update({ 
          status: 'revision_requested',
          notes: revisionNote.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', order.id);

      if (error) throw error;

      // Send notification email to vendor
      try {
        await invokeEdgeFunction('send-email', {
          body: {
            email_type: 'custom_notification',
            to_email: order.vendor?.email || 'vendor@example.com',
            data: {
              name: 'Video Vendor',
              subject: 'Video Revision Requested',
              message: `
                <h2>Video Revision Requested</h2>
                <p>A revision has been requested for order #${order.id.slice(-8)}</p>
                <p><strong>Player:</strong> ${order.player_name}</p>
                <p><strong>Customer:</strong> ${order.purchaser_name}</p>
                <p><strong>Revision Details:</strong></p>
                <p>${revisionNote.trim()}</p>
                <p>Please make the necessary changes and resubmit the video.</p>
              `,
              player_name: order.player_name
            }
          }
        });
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
      }

      // Send SMS notification to customer confirming revision request
      if (order.purchaser_phone) {
        try {
          await invokeEdgeFunction('send-sms', {
            body: {
              to_phone: order.purchaser_phone,
              message: `Hi ${order.purchaser_name}! Your revision request for ${order.player_name}'s video has been sent to the vendor. You'll be notified when it's completed.`
            }
          });
        } catch (smsError) {
          console.error('Failed to send SMS notification:', smsError);
        }
      }

      toast.success('Revision request sent successfully!');
      setRevisionNote('');
      onOrderUpdate?.();
    } catch (error) {
      console.error('Error requesting revision:', error);
      toast.error('Failed to send revision request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Order Details
          </DialogTitle>
          <DialogDescription>
            Order #{order.id.slice(-8)} - Complete order information
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Status & Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Order Status
                </span>
                <Badge variant={getStatusColor(order.status)}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1).replace('_', ' ')}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order Created</p>
                  <p className="font-medium">{formatDate(order.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Updated</p>
                  <p className="font-medium">{formatDate(order.updated_at)}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-bold text-primary">{formatPrice(order.total_amount_cents)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Player & Team Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Player Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Player Name</p>
                <p className="font-medium text-lg">{order.player_name}</p>
              </div>
              {order.team_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Team</p>
                  <p className="font-medium">{order.team_name}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Customer Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Customer Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{order.purchaser_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {order.purchaser_email}
                </p>
              </div>
              {order.purchaser_phone && (
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {order.purchaser_phone}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Event Information */}
          {order.event && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Event Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Event Name</p>
                  <p className="font-medium">{order.event.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Event Date</p>
                  <p className="font-medium">{formatDate(order.event.start_date)}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Package Information */}
          {order.package && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Package Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Package Type</p>
                  <p className="font-medium capitalize">{order.package.package_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Package Price</p>
                  <p className="font-medium">{formatPrice(order.package.price_cents)}</p>
                </div>
                {order.package.description && (
                  <div>
                    <p className="text-sm text-muted-foreground">Description</p>
                    <p className="font-medium">{order.package.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {order.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Order Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{order.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Video Management Section */}
          {(order.status === 'pending' || order.status === 'confirmed' || order.status === 'in_progress' || order.status === 'revision_requested') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Submit Video
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Video URL</label>
                  <Input
                    placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste the link to your completed video (YouTube, Vimeo, Google Drive, etc.)
                  </p>
                </div>
                <Button 
                  onClick={handleVideoSubmit} 
                  disabled={isSubmitting || !videoUrl.trim()}
                  className="w-full"
                >
                  <Link className="h-4 w-4 mr-2" />
                  {isSubmitting ? 'Submitting...' : 'Submit Video URL'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Video URL Display */}
          {order.video_url && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Link className="h-4 w-4" />
                    Completed Video
                  </span>
                  {order.status === 'completed' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setRevisionNote('')}
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Request Revision
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Video URL</p>
                  <a 
                    href={order.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-mono text-sm bg-muted p-2 rounded break-all block hover:bg-muted/80 transition-colors"
                  >
                    {order.video_url}
                  </a>
                </div>
                
                {/* Revision Request Form */}
                {revisionNote !== undefined && (
                  <div className="border-t pt-4 space-y-3">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Revision Details</label>
                      <Textarea
                        placeholder="Please describe what changes you'd like to see in the video..."
                        value={revisionNote}
                        onChange={(e) => setRevisionNote(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleRevisionRequest} 
                        disabled={isSubmitting || !revisionNote.trim()}
                        variant="outline"
                        size="sm"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Revision Request'}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setRevisionNote(undefined)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailsModal;